import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  IconButton,
  Paper,
  Collapse,
  Grid,
  Typography,
  Switch,
  FormControlLabel,
  InputAdornment,
  Autocomplete,
  Slider,
  Divider,
} from '@mui/material';
import {
  Search,
  FilterList,
  Clear,
  ExpandMore,
  ExpandLess,
  PlaylistPlay,
  Edit,
  Archive,
  Emergency,
  Assignment,
  PlayCircle,
  AccessTime,
  Person,
} from '@mui/icons-material';

import { PlaylistType, PlaylistStatus } from "../../types";
import type { SearchFilters, SortOptions } from "../../types";

interface PlaylistFiltersProps {
  filters: SearchFilters;
  sortOptions: SortOptions;
  onFiltersChange: (filters: SearchFilters) => void;
  onSortChange: (sort: SortOptions) => void;
  onClearFilters: () => void;
  totalItems?: number;
  availableAuthors?: { id: number; name: string }[];
}

const PlaylistFilters: React.FC<PlaylistFiltersProps> = ({
  filters,
  sortOptions,
  onFiltersChange,
  onSortChange,
  onClearFilters,
  totalItems = 0,
  availableAuthors = [],
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      search: event.target.value,
    });
  };

  const handleStatusChange = (event: any) => {
    const value = event.target.value;
    onFiltersChange({
      ...filters,
      playlistStatuses: typeof value === 'string' ? value.split(',') : value,
    });
  };

  const handleTypeChange = (event: any) => {
    const value = event.target.value;
    onFiltersChange({
      ...filters,
      playlistTypes: typeof value === 'string' ? value.split(',') : value,
    });
  };

  const handleAuthorChange = (event: any, newValue: any) => {
    onFiltersChange({
      ...filters,
      createdBy: newValue?.id || undefined,
    });
  };

  const handleDurationRangeChange = (event: Event, newValue: number | number[]) => {
    onFiltersChange({
      ...filters,
      durationRange: newValue as [number, number],
    });
  };

  const handleActiveOnlyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      activeOnly: event.target.checked,
    });
  };

  const handleDateFromChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      dateFrom: event.target.value,
    });
  };

  const handleDateToChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      dateTo: event.target.value,
    });
  };

  const handleSortFieldChange = (event: any) => {
    onSortChange({
      ...sortOptions,
      field: event.target.value,
    });
  };

  const handleSortDirectionChange = (event: any) => {
    onSortChange({
      ...sortOptions,
      direction: event.target.value,
    });
  };

  const getStatusLabel = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'draft': return 'Черновик';
      case 'active': return 'Активный';
      case 'archived': return 'Архивированный';
      default: return 'Активный';
    }
  };

  const getTypeLabel = (type: string): string => {
    switch (type.toLowerCase()) {
      case 'standard': return 'Стандартный';
      case 'emergency': return 'Экстренный';
      case 'template': return 'Шаблон';
      default: return 'Стандартный';
    }
  };

  const getActiveFiltersCount = (): number => {
    let count = 0;
    if (filters.search) count++;
    if (filters.playlistStatuses && filters.playlistStatuses.length > 0) count++;
    if (filters.playlistTypes && filters.playlistTypes.length > 0) count++;
    if (filters.createdBy) count++;
    if (filters.durationRange) count++;
    if (filters.activeOnly) count++;
    if (filters.dateFrom) count++;
    if (filters.dateTo) count++;
    return count;
  };

  const getStatusIcon = (status: PlaylistStatus) => {
    switch (status) {
      case PlaylistStatus.ACTIVE:
        return <PlayCircle fontSize="small" />;
      case PlaylistStatus.DRAFT:
        return <Edit fontSize="small" />;
      case PlaylistStatus.ARCHIVED:
        return <Archive fontSize="small" />;
      default:
        return <PlaylistPlay fontSize="small" />;
    }
  };

  const getTypeIcon = (type: PlaylistType) => {
    switch (type) {
      case PlaylistType.EMERGENCY:
        return <Emergency fontSize="small" />;
      case PlaylistType.TEMPLATE:
        return <Assignment fontSize="small" />;
      case PlaylistType.STANDARD:
      default:
        return <PlaylistPlay fontSize="small" />;
    }
  };

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds % 60 > 0 ? (seconds % 60).toString().padStart(2, '0') : '00'}`;
  };

  const activeFiltersCount = getActiveFiltersCount();

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
      {/* Основная строка фильтров */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: expanded ? 2 : 0 }}>
        {/* Поиск */}
        <TextField
          placeholder={t('playlists.searchPlaceholder')}
          value={filters.search || ''}
          onChange={handleSearchChange}
          variant="outlined"
          size="small"
          sx={{ flexGrow: 1, maxWidth: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: filters.search && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => onFiltersChange({ ...filters, search: '' })}
                >
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Статус */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t('common.status')}</InputLabel>
          <Select
            multiple
            value={filters.playlistStatuses || []}
            onChange={handleStatusChange}
            label={t('common.status')}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as string[]).map((value) => (
                                      <Chip
                      key={value}
                      label={getStatusLabel(value)}
                      size="small"
                      icon={getStatusIcon(value as PlaylistStatus)}
                      color={
                        value === PlaylistStatus.ACTIVE ? 'success' :
                        value === PlaylistStatus.DRAFT ? 'warning' : 'default'
                      }
                    />
                ))}
              </Box>
            )}
          >
            {Object.values(PlaylistStatus).map((status) => (
              <MenuItem key={status} value={status}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getStatusIcon(status)}
                  {getStatusLabel(status)}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Тип плейлиста */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t('playlists.typeLabel')}</InputLabel>
          <Select
            multiple
            value={filters.playlistTypes || []}
            onChange={handleTypeChange}
            label={t('playlists.typeLabel')}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as string[]).map((value) => (
                                      <Chip
                      key={value}
                      label={getTypeLabel(value)}
                      size="small"
                      icon={getTypeIcon(value as PlaylistType)}
                      color={
                        value === PlaylistType.EMERGENCY ? 'error' :
                        value === PlaylistType.TEMPLATE ? 'info' : 'primary'
                      }
                    />
                ))}
              </Box>
            )}
          >
            {Object.values(PlaylistType).map((type) => (
              <MenuItem key={type} value={type}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getTypeIcon(type)}
                  {getTypeLabel(type)}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Сортировка */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{t('common.sortBy')}</InputLabel>
          <Select
            value={sortOptions.field || 'name'}
            onChange={handleSortFieldChange}
            label={t('common.sortBy')}
          >
            <MenuItem value="name">{t('playlists.name')}</MenuItem>
            <MenuItem value="status">{t('common.status')}</MenuItem>
            <MenuItem value="type">{t('playlists.typeLabel')}</MenuItem>
            <MenuItem value="items_count">{t('playlists.itemsCount')}</MenuItem>
            <MenuItem value="total_duration">{t('playlists.duration')}</MenuItem>
            <MenuItem value="created_at">{t('common.dateCreated')}</MenuItem>
            <MenuItem value="updated_at">{t('common.dateUpdated')}</MenuItem>
            <MenuItem value="last_played">{t('playlists.lastPlayed')}</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>{t('common.order')}</InputLabel>
          <Select
            value={sortOptions.direction || 'asc'}
            onChange={handleSortDirectionChange}
            label={t('common.order')}
          >
            <MenuItem value="asc">{t('common.ascending')}</MenuItem>
            <MenuItem value="desc">{t('common.descending')}</MenuItem>
          </Select>
        </FormControl>

        {/* Кнопки управления */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterList />}
            endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
            onClick={() => setExpanded(!expanded)}
            color={activeFiltersCount > 0 ? 'primary' : 'inherit'}
          >
            {t('common.filters')}
            {activeFiltersCount > 0 && (
              <Chip
                label={activeFiltersCount}
                size="small"
                color="primary"
                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Button>

          {activeFiltersCount > 0 && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<Clear />}
              onClick={onClearFilters}
              color="error"
            >
              {t('common.clear')}
            </Button>
          )}
        </Box>
      </Box>

      {/* Расширенные фильтры */}
      <Collapse in={expanded}>
        <Grid container spacing={2}>
          {/* Автор */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Autocomplete
              options={availableAuthors}
              getOptionLabel={(option) => option.name}
              value={availableAuthors.find(author => author.id === filters.createdBy) || null}
              onChange={handleAuthorChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('playlists.author')}
                  placeholder={t('playlists.selectAuthor')}
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {/* Диапазон длительности */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('playlists.durationRange')}
            </Typography>
            <Box sx={{ px: 1 }}>
              <Slider
                value={filters.durationRange || [0, 3600]}
                onChange={handleDurationRangeChange}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => formatDuration(value)}
                min={0}
                max={3600} // 1 час
                step={60} // 1 минута
                marks={[
                  { value: 0, label: '0' },
                  { value: 900, label: '15м' },
                  { value: 1800, label: '30м' },
                  { value: 3600, label: '1ч' },
                ]}
              />
            </Box>
          </Grid>

          {/* Диапазон дат */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('playlists.dateRange')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
              <TextField
                label={t('common.dateFrom')}
                type="date"
                size="small"
                value={filters.dateFrom || ''}
                onChange={handleDateFromChange}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label={t('common.dateTo')}
                type="date"
                size="small"
                value={filters.dateTo || ''}
                onChange={handleDateToChange}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Grid>

          {/* Быстрые фильтры */}
          <Grid size={{ xs: 12 }}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" gutterBottom>
              {t('playlists.quickFilters')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={filters.activeOnly || false}
                    onChange={handleActiveOnlyChange}
                    size="small"
                  />
                }
                label={t('playlists.activeOnly')}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={filters.playlistTypes?.includes(PlaylistType.EMERGENCY) || false}
                    onChange={(e) => {
                      const emergencyTypes = e.target.checked 
                        ? [PlaylistType.EMERGENCY]
                        : [];
                      onFiltersChange({
                        ...filters,
                        playlistTypes: emergencyTypes,
                      });
                    }}
                    size="small"
                  />
                }
                label={t('playlists.emergencyOnly')}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={filters.playlistTypes?.includes(PlaylistType.TEMPLATE) || false}
                    onChange={(e) => {
                      const templateTypes = e.target.checked 
                        ? [PlaylistType.TEMPLATE]
                        : [];
                      onFiltersChange({
                        ...filters,
                        playlistTypes: templateTypes,
                      });
                    }}
                    size="small"
                  />
                }
                label={t('playlists.templatesOnly')}
              />
            </Box>
          </Grid>

          {/* Статистика */}
          <Grid size={{ xs: 12 }}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" gutterBottom>
              {t('playlists.statistics')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary">
                {t('playlists.totalPlaylists')}: {totalItems}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('playlists.activePlaylists')}: {Math.floor(totalItems * 0.6)} {/* Пример */}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('playlists.draftPlaylists')}: {Math.floor(totalItems * 0.3)} {/* Пример */}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('playlists.archivedPlaylists')}: {Math.floor(totalItems * 0.1)} {/* Пример */}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Информация о результатах */}
        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            {t('common.showingResults', { total: totalItems })}
          </Typography>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default PlaylistFilters; 
 
 
 